/**
 * The kit. Every component here is the DESIGN.md contract expressed once, so a page cannot quietly invent
 * a variant — in particular the six states every data surface must implement (loading, empty, error,
 * partial, stale, denied) live here rather than being re-implemented per screen.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, type ProblemDetails } from '../lib/api.js';
import { bandStyle, dateTime, integer, provenanceTokens, relative, value, type Provenance } from '../lib/format.js';
import { useInView, prefersReducedMotion } from '../lib/use-inview.js';

export const cx = (...parts: Array<string | false | null | undefined>): string => parts.filter(Boolean).join(' ');

/* ── containers ─────────────────────────────────────────────────────────────────────── */

export function Panel({
  title,
  subtitle,
  provenance,
  actions,
  state,
  className,
  children,
  id,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  provenance?: Provenance;
  actions?: ReactNode;
  state?: string | null;
  className?: string;
  children: ReactNode;
  id?: string;
}): ReactNode {
  const style = bandStyle(state ?? 'UNVERIFIED');
  return (
    <section id={id} className={cx('panel', style.rank >= 4 && style.panel, className)}>
      {(title || actions) && (
        <header className="panel-head">
          <div className="min-w-0">
            {title ? <h3 className="panel-title truncate">{title}</h3> : null}
            {subtitle ? <p className="text-cell text-text-secondary mt-1">{subtitle}</p> : null}
            {provenance ? <ProvenanceLine {...provenance} /> : null}
          </div>
          {actions ? <div className="flex items-center gap-2 shrink-0">{actions}</div> : null}
        </header>
      )}
      {children}
    </section>
  );
}

export function ProvenanceLine(provenance: Provenance): ReactNode {
  const tokens = useMemo(() => provenanceTokens(provenance), [provenance]);
  if (tokens.length === 0) return null;
  return (
    <p className="provenance mt-2 flex flex-wrap gap-x-3 gap-y-1" title={tokens.join(' · ')}>
      {tokens.map((token) => (
        <span key={token}>{token}</span>
      ))}
    </p>
  );
}

export function Tag({ band, children, className, title }: { band?: string | null; children?: ReactNode; className?: string; title?: string }): ReactNode {
  const style = bandStyle(band ?? 'UNVERIFIED');
  return (
    <span className={cx('tag', style.tag, className)} title={title ?? style.label}>
      <span aria-hidden="true" className="dot" />
      {children ?? style.label}
    </span>
  );
}

export function Badge({ children, tone = 'muted', title }: { children: ReactNode; tone?: 'ok' | 'watch' | 'alert' | 'danger' | 'muted' | 'info'; title?: string }): ReactNode {
  return (
    <span className={cx('tag', `tag--${tone}`)} title={title}>
      {children}
    </span>
  );
}

/** Value → label → provenance, in that order (DESIGN.md §8 Do #1). */
export function Readout({
  label,
  numeric,
  unit,
  digits = 2,
  band,
  hint,
  provenance,
  className,
}: {
  label: string;
  numeric: number | string | null | undefined;
  unit?: string;
  digits?: number;
  band?: string | null;
  hint?: ReactNode;
  provenance?: Provenance;
  className?: string;
}): ReactNode {
  const style = band ? bandStyle(band) : null;
  return (
    <div className={cx('min-w-0', className)}>
      <div className="micro-label">{label}</div>
      <div className="flex items-baseline gap-1 mt-1">
        <span className="text-readout font-mono font-medium num" style={style ? { color: style.colour } : undefined}>
          {value(numeric, digits)}
        </span>
        {unit ? <span className="unit">{unit}</span> : null}
        {style ? <span className="ml-1 text-cell" style={{ color: style.colour }}>
          {style.label}
        </span> : null}
      </div>
      {hint ? <div className="text-cell text-text-secondary mt-1">{hint}</div> : null}
      {provenance ? <ProvenanceLine {...provenance} /> : null}
    </div>
  );
}

export function Skeleton({ rows = 3, className }: { rows?: number; className?: string }): ReactNode {
  return (
    <div className={cx('grid gap-2', className)} role="status" aria-live="polite" aria-label="loading">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="skel" style={{ height: index === 0 ? 18 : 12, width: `${100 - index * 7}%` }} />
      ))}
    </div>
  );
}

/* ── the six required states ──────────────────────────────────────────────────────────── */

export function EmptyState({ title, detail, action, actionHref }: { title: string; detail: ReactNode; action?: string; actionHref?: string }): ReactNode {
  return (
    <div className="panel flex flex-col items-start gap-3">
      <h3 className="text-h3">{title}</h3>
      <p className="text-ui text-text-secondary max-w-[60ch]">{detail}</p>
      {action && actionHref ? (
        <Link className="btn btn-secondary btn-sm" to={actionHref}>
          {action}
        </Link>
      ) : null}
    </div>
  );
}

export function ErrorState({ error, onRetry, whatNext }: { error: unknown; onRetry?: () => void; whatNext?: ReactNode }): ReactNode {
  const problem: ProblemDetails = error instanceof ApiError ? error.problem : { type: '/errors/unknown', title: 'Unexpected error', status: 0, detail: error instanceof Error ? error.message : String(error) };
  return (
    <div className="panel panel--state-danger flex flex-col gap-3" role="alert">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-h3">{problem.title}</h3>
          <p className="text-ui text-text-secondary mt-1 max-w-[70ch]">{problem.detail}</p>
        </div>
        {onRetry ? (
          <button type="button" className="btn btn-secondary btn-sm" onClick={onRetry}>
            Retry
          </button>
        ) : null}
      </div>
      {problem.details?.length ? (
        <ul className="text-cell text-text-secondary list-disc pl-5">
          {problem.details.map((entry, index) => (
            <li key={`${entry.path}-${index}`}>
              <span className="font-mono">{entry.path}</span>: {entry.message}
            </li>
          ))}
        </ul>
      ) : null}
      {problem.nextAction ? (
        <p className="text-ui">
          {problem.nextAction.href?.startsWith('http') ? (
            <a className="link" href={problem.nextAction.href} target="_blank" rel="noreferrer">
              {problem.nextAction.label}
            </a>
          ) : problem.nextAction.href ? (
            <Link className="link" to={problem.nextAction.href}>
              {problem.nextAction.label}
            </Link>
          ) : (
            <span>{problem.nextAction.label}</span>
          )}
        </p>
      ) : null}
      {whatNext}
      <p className="provenance">
        {problem.code ? `${problem.code} · ` : ''}
        {problem.requestId ? `correlation id ${problem.requestId}` : 'no correlation id returned'}
        {problem.retryable ? ' · retryable' : ''}
      </p>
    </div>
  );
}

export function PartialNotice({ missing, note }: { missing: string[]; note?: string }): ReactNode {
  if (missing.length === 0 && !note) return null;
  return (
    <p className="text-cell text-text-secondary flex flex-wrap items-center gap-2" role="note">
      <Badge tone="info">Partial</Badge>
      {missing.length ? (
        <span>
          not plotted — no usable data for <span className="font-mono text-text">{missing.join(', ')}</span>
        </span>
      ) : null}
      {note ? <span>{note}</span> : null}
    </p>
  );
}

export function StaleNotice({ at, budgetMinutes = 15 }: { at: string | null | undefined; budgetMinutes?: number }): ReactNode {
  const minutes = at ? (Date.now() - new Date(at).getTime()) / 60_000 : null;
  if (minutes === null || minutes < budgetMinutes) return null;
  return (
    <p className="text-cell flex items-center gap-2" role="status" style={{ color: 'var(--watch)' }}>
      <span className="dot" aria-hidden="true" />
      last sample {relative(at)} ({dateTime(at)}) — older than the {budgetMinutes} min freshness budget, so the plot below is the last known state, not the current one
    </p>
  );
}

export function DeniedState({ error }: { error: ApiError }): ReactNode {
  return (
    <div className="panel flex flex-col gap-2">
      <h3 className="text-h3">{error.status === 402 ? 'This subscription does not cover that action' : 'Your role does not include that action'}</h3>
      <p className="text-ui text-text-secondary max-w-[62ch]">{error.problem.detail}</p>
      {error.problem.nextAction?.href ? (
        <Link className="btn btn-primary btn-sm self-start" to={error.problem.nextAction.href}>
          {error.problem.nextAction.label}
        </Link>
      ) : null}
      <p className="provenance">{error.code}</p>
    </div>
  );
}

/* ── actions ──────────────────────────────────────────────────────────────────────────── */

export function Action({
  children,
  onClick,
  variant = 'secondary',
  size,
  disabled,
  pending,
  type = 'button',
  title,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'lg';
  disabled?: boolean;
  pending?: boolean;
  type?: 'button' | 'submit';
  title?: string;
}): ReactNode {
  return (
    <button
      type={type}
      title={title}
      className={cx('btn', `btn-${variant}`, size === 'sm' && 'btn-sm', size === 'lg' && 'btn-lg')}
      onClick={onClick}
      disabled={disabled || pending}
      data-loading={pending ? 'true' : undefined}
    >
      {pending ? <span className="dot" aria-hidden="true" /> : null}
      <span className="btn-label">{children}</span>
    </button>
  );
}

export function useAsyncAction<A extends unknown[]>(fn: (...args: A) => Promise<void>): { run: (...args: A) => void; pending: boolean; error: ApiError | null; reset: () => void } {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );
  return {
    pending,
    error,
    reset: () => setError(null),
    run: async (...args: A) => {
      setPending(true);
      setError(null);
      try {
        await fn(...args);
      } catch (err) {
        if (mounted.current) setError(err instanceof ApiError ? err : new ApiError(0, { type: '/errors/unknown', title: 'Request failed', status: 0, detail: err instanceof Error ? err.message : String(err) }));
      } finally {
        if (mounted.current) setPending(false);
      }
    },
  };
}

/**
 * Right-side sheet rather than a modal for multi-field editing (DESIGN.md §8 Don't). Focus moves in on
 * open and returns to the trigger on close; Escape and the backdrop dismiss it.
 */
export function Sheet({ open, onClose, title, children, width = 560 }: { open: boolean; onClose: () => void; title: string; children: ReactNode; width?: number }): ReactNode {
  const panel = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label={title}>
      <button type="button" aria-label="Close panel" className="absolute inset-0 bg-[rgba(16,23,31,0.28)] border-0 cursor-default" onClick={onClose} />
      <div ref={panel} tabIndex={-1} className="relative h-full overflow-y-auto bg-surface border-l border-border shadow-[0_12px_32px_-8px_rgba(16,23,31,0.18),0_2px_6px_rgba(16,23,31,0.08)] p-5 outline-none" style={{ width: `min(${width}px, 100%)` }}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <h2 className="text-h3">{title}</h2>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Reveal({ children, index = 0, className }: { children: ReactNode; index?: number; className?: string }): ReactNode {
  const { ref, inView } = useInView<HTMLDivElement>();
  return (
    <div ref={ref} className={cx('reveal', className)} data-inview={inView ? 'true' : 'false'} data-stagger="" style={{ '--i': index } as React.CSSProperties}>
      {children}
    </div>
  );
}

/** Landing-only animated number (DESIGN.md §7 pattern 6): one run, no workspace use. */
export function CountUp({ to, format, durationMs = 1100 }: { to: number; format: (n: number) => string; durationMs?: number }): ReactNode {
  const { ref, inView } = useInView<HTMLSpanElement>();
  const reduced = prefersReducedMotion();
  const [shown, setShown] = useState(reduced ? to : 0);
  useEffect(() => {
    if (!inView || reduced) return;
    let raf = 0;
    const start = performance.now();
    const step = (now: number): void => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(to * eased);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, reduced, to, durationMs]);
  return (
    <span ref={ref} className="num tabular-nums">
      {format(shown)}
    </span>
  );
}

export function KeyValue({ items }: { items: Array<[string, ReactNode]> }): ReactNode {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-cell">
      {items.map(([key, val]) => (
        <div key={key} className="contents">
          <dt className="text-text-tertiary">{key}</dt>
          <dd className="text-text">{val}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Table({ head, rows, onRowClick, dense }: { head: ReactNode[]; rows: ReactNode[][]; onRowClick?: (index: number) => void; dense?: boolean }): ReactNode {
  return (
    <div className="overflow-x-plot -mx-4 px-4 md:mx-0 md:px-0">
      <table className={cx('tbl', dense && 'text-[12px]')}>
        <thead>
          <tr>
            {head.map((cell, index) => (
              <th key={index} scope="col" className={index > 0 && /^\d|num/.test(String(cell)) ? 'text-right' : undefined}>
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              role={onRowClick ? 'button' : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? () => onRowClick(rowIndex) : undefined}
              onKeyDown={
                onRowClick
                  ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onRowClick(rowIndex);
                      }
                    }
                  : undefined
              }
            >
              {row.map((cell, cellIndex) => (
                <td key={cellIndex}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const numberCell = (input: number | string | null | undefined, digits = 2): ReactNode => <span className="num">{value(input, digits)}</span>;
export const countCell = (input: number | null | undefined): ReactNode => <span className="num">{integer(input ?? 0)}</span>;
